import { useParams, Link, useNavigate } from 'react-router-dom';
import { sugarData, getSugarById, sugarCategoryLabels } from '../data/sugarData';
import { ChevronRight, ChevronDown, Candy, Factory, Utensils, Package, Award, MessageSquareQuote } from 'lucide-react';
import { useState } from 'react';

// AI-Generated sugar images
const sugarImages = {
  'icumsa-45': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/66445efc0d828bce8a60cb23e72e1e2edffe3626719afecc490acb033d435a63.png',
  'icumsa-100': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/525eeb5bb71ce4020d705669c9300b8f4e945ff8c8bf965cb790c014146dc2fe.png',
  'icumsa-150': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/aac68068db0f5ec885e7a4cf4ae77df7c722c7dce317f869971a47252a855407.png',
  'raw-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/58483224c19433396882d43eb91cf1a7d4018dbfdf9167dfbf572a7cfc861bad.png',
  'plantation-white': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/7e3d08efecd864348a9231d4fe4580c30c9a158c59b13376115432798ba9cd96.png',
  'brown-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/7a94b6fc85c9f33323cbef4c807b56ea19fd4fbf8f24394db6b07e143e2d1f14.png',
  'demerara-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/539ab2a2665b27a164286a742e5da28700b9babc99814e2af40da36b84b9fd59.png',
  'muscovado-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/16988f91e249e90c10938aa004b4fad88a79a09f994dec76e169e89ca9786744.png',
  'jaggery-powder': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/05daff6cee1591796f76cd955d15666f5bd09505af3b17ed90781c6bf9f12046.png',
  'jaggery-blocks': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/0c903422c216c083f4f59ca97e63a7821df40f2073d74b168f9cf35294468e67.png',
  'palm-jaggery': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/2e3c823fc37b03eaca959c89a132199b901d933918702601e12adb49d6ca562b.png',
  'caster-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/3fcd6d393195f2fcfd93710bd0c74442c88b3f56c7bde5b4d8d1b03b490bbba7.png',
};

const getSugarImage = (imageKey) => {
  return sugarImages[imageKey] || 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=800&h=500&fit=crop';
};

export default function SugarDetail() {
  const { sugarId } = useParams();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const sugar = getSugarById(sugarId);

  if (!sugar) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-4">Product Not Found</h1>
          <Link to="/products/sugar" className="text-[#F47D20] hover:underline">
            Return to Sugar Catalogue
          </Link>
        </div>
      </div>
    );
  }

  const handleSugarChange = (newSugarId) => {
    setIsDropdownOpen(false);
    navigate(`/products/sugar/${newSugarId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#263776] via-[#1e2d5f] to-[#263776] text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#F47D20] rounded-full filter blur-3xl translate-x-1/2 -translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-4 py-8 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm mb-6 text-gray-300">
            <Link to="/" className="hover:text-[#F47D20] transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/products" className="hover:text-[#F47D20] transition-colors">Products</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/products/sugar" className="hover:text-[#F47D20] transition-colors">Sugar</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#F47D20]">{sugar.name}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Image */}
            <div className="w-full lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getSugarImage(sugar.image)}
                  alt={sugar.name}
                  className="w-full h-64 md:h-80 object-cover"
                />
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-[#F47D20] text-white text-sm font-medium rounded-full">
                  {sugarCategoryLabels[sugar.category]}
                </div>
              </div>
            </div>

            {/* Title and Quick Info */}
            <div className="w-full lg:w-1/2">
              {/* Sugar Dropdown Selector */}
              <div className="relative mb-6">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center justify-between w-full md:w-80 px-4 py-3 bg-white/10 backdrop-blur border border-white/20 rounded-lg text-left hover:bg-white/20 transition-colors"
                  data-testid="sugar-dropdown-toggle"
                >
                  <span className="text-sm">Browse Other Products</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-full md:w-80 max-h-64 overflow-y-auto bg-white rounded-lg shadow-xl z-50 border border-gray-200">
                    {sugarData.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => handleSugarChange(s.id)}
                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          s.id === sugar.id ? 'bg-[#F47D20]/10 text-[#F47D20]' : 'text-gray-700'
                        }`}
                      >
                        <span>{s.name}</span>
                        {s.id === sugar.id && <span className="text-xs">Current</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-2">{sugar.name}</h1>
              <p className="text-xl text-gray-300 mb-6">{sugar.grade}</p>
              
              {/* Quick Info Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">ICUMSA Value</div>
                  <div className="font-semibold">{sugar.icumsaValue}</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Origin</div>
                  <div className="font-semibold text-sm">{sugar.origin.split(' - ')[0]}</div>
                </div>
              </div>

              {/* Get Quote Button */}
              <Link
                to={`/contact?product=${encodeURIComponent(sugar.name)}&type=Quote%20Request`}
                className="inline-flex items-center px-6 py-3 bg-[#F47D20] text-white font-semibold rounded-lg hover:bg-[#e06d10] transition-colors shadow-lg"
                data-testid="get-quote-btn"
              >
                <MessageSquareQuote className="w-5 h-5 mr-2" />
                Get Quote for {sugar.name}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <section className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#263776]/10 rounded-lg flex items-center justify-center">
                  <Candy className="w-5 h-5 text-[#263776]" />
                </div>
                <h2 className="text-2xl font-bold text-[#263776]">Description</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">{sugar.description}</p>
            </section>

            {/* Processing */}
            <section className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#F47D20]/10 rounded-lg flex items-center justify-center">
                  <Factory className="w-5 h-5 text-[#F47D20]" />
                </div>
                <h2 className="text-2xl font-bold text-[#263776]">Processing</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">{sugar.processing}</p>
            </section>

            {/* Applications */}
            <section className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Utensils className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#263776]">Applications & Uses</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">{sugar.applications}</p>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Specifications */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-bold text-[#263776]">Specifications</h2>
              </div>
              <div className="space-y-2">
                {Object.entries(sugar.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-gray-500 text-sm capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="font-medium text-gray-800 text-sm">{value}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Certifications */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-bold text-[#263776]">Certifications</h2>
              </div>
              <p className="text-gray-600 text-sm">{sugar.certifications}</p>
            </section>

            {/* Packaging & Shelf Life */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-[#263776] mb-3">Packaging</h2>
              <p className="text-gray-600 text-sm mb-4">{sugar.packaging}</p>
              <h2 className="text-lg font-bold text-[#263776] mb-3">Shelf Life</h2>
              <p className="text-gray-600 text-sm">{sugar.shelfLife}</p>
            </section>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-[#263776] to-[#1e2d5f] rounded-xl p-6 text-white">
              <h3 className="text-lg font-bold mb-3">Interested in {sugar.name}?</h3>
              <p className="text-gray-300 text-sm mb-4">
                Get competitive pricing, bulk quantities, and custom specifications for your business needs.
              </p>
              <Link
                to={`/contact?product=${encodeURIComponent(sugar.name)}&type=Quote%20Request`}
                className="block w-full py-3 bg-[#F47D20] text-center font-semibold rounded-lg hover:bg-[#e06d10] transition-colors"
                data-testid="sidebar-get-quote-btn"
              >
                Request Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products */}
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#263776] mb-6">Related Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {sugarData
              .filter(s => s.id !== sugar.id && s.category === sugar.category)
              .slice(0, 6)
              .map((relatedSugar) => (
                <Link
                  key={relatedSugar.id}
                  to={`/products/sugar/${relatedSugar.id}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all"
                >
                  <div className="h-24 overflow-hidden">
                    <img
                      src={getSugarImage(relatedSugar.image)}
                      alt={relatedSugar.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-sm text-[#263776] group-hover:text-[#F47D20] transition-colors truncate">
                      {relatedSugar.name}
                    </h3>
                  </div>
                </Link>
              ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/products/sugar"
              className="inline-flex items-center px-6 py-3 border-2 border-[#263776] text-[#263776] font-semibold rounded-lg hover:bg-[#263776] hover:text-white transition-colors"
            >
              View Full Catalogue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
